import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  criarConfiguracaoListagem,
  excluirConfiguracaoListagem,
  listarConfiguracoesListagem,
  obterConfiguracaoListagemPadrao
} from '@spark/services/configuracoes-listagem';
import { notification } from '../../utils/notification';
import { CONFIG_PADRAO_TIMEOUT_MS } from '../../utils/listagemConfiguracaoConstants';
import {
  buildColunasParaPersistencia,
  buildFiltrosParaPersistencia,
  buildOrdenacaoParaPersistencia,
  extractConfiguracaoListagemError,
  mapConfiguracaoParaEstadoListagem
} from '../../utils/configuracoesListagemMapper';

export const useListagemConfiguracao = ({
  listagemConfig,
  filters,
  values,
  enabled,
  podeSalvarGeral,
  onApplyConfiguracao,
  onConfiguracaoReady,
  obterValoresAtuais
}) => {
  const [configs, setConfigs] = useState([]);
  const [loadingListagem, setLoadingListagem] = useState(false);
  const [loadingPadrao, setLoadingPadrao] = useState(false);
  const [loadingSalvar, setLoadingSalvar] = useState(false);
  const [loadingExcluirId, setLoadingExcluirId] = useState(null);
  const [showListagemModal, setShowListagemModal] = useState(false);
  const [showSalvarModal, setShowSalvarModal] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState(undefined);
  const [columnVisibilityOverride, setColumnVisibilityOverride] = useState(undefined);
  const columnLayoutSnapshotRef = useRef(undefined);

  const onApplyConfiguracaoRef = useRef(onApplyConfiguracao);
  const onConfiguracaoReadyRef = useRef(onConfiguracaoReady);
  const valuesRef = useRef(values);
  const visibleColumnIdsRef = useRef(visibleColumnIds);
  const columnVisibilityOverrideRef = useRef(columnVisibilityOverride);

  useEffect(() => {
    onApplyConfiguracaoRef.current = onApplyConfiguracao;
  }, [onApplyConfiguracao]);

  useEffect(() => {
    onConfiguracaoReadyRef.current = onConfiguracaoReady;
  }, [onConfiguracaoReady]);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    visibleColumnIdsRef.current = visibleColumnIds;
  }, [visibleColumnIds]);

  useEffect(() => {
    columnVisibilityOverrideRef.current = columnVisibilityOverride;
  }, [columnVisibilityOverride]);

  const rotaListagem = listagemConfig?.rotaListagem;

  const handleAplicarConfiguracao = useCallback((config) => {
    const estado = mapConfiguracaoParaEstadoListagem(config, filters, listagemConfig);
    onApplyConfiguracaoRef.current?.(estado.filtros);
    setVisibleColumnIds(estado.visibleColumnIds);
    setColumnVisibilityOverride(estado.columnVisibility);
  }, [filters, listagemConfig]);

  const listarConfigs = useCallback(async () => {
    if (!enabled || !rotaListagem) {
      return [];
    }

    setLoadingListagem(true);

    try {
      const response = await listarConfiguracoesListagem(rotaListagem);
      const registros = Array.isArray(response?.registros) ? response.registros : [];
      setConfigs(registros);
      return registros;
    } catch (error) {
      console.error('Erro ao listar configuracoes de listagem:', error);
      notification.error(extractConfiguracaoListagemError(error), 'Configuracoes');
      setConfigs([]);
      return [];
    } finally {
      setLoadingListagem(false);
    }
  }, [enabled, rotaListagem]);

  useEffect(() => {
    if (!enabled || !rotaListagem) {
      onConfiguracaoReadyRef.current?.();
      return undefined;
    }

    let cancelled = false;
    let readyNotificado = false;

    const notificarReadyUmaVez = () => {
      if (readyNotificado || cancelled) {
        return;
      }

      readyNotificado = true;
      onConfiguracaoReadyRef.current?.();
    };

    const timeoutId = window.setTimeout(() => {
      notificarReadyUmaVez();
    }, CONFIG_PADRAO_TIMEOUT_MS);

    const carregarPadrao = async () => {
      setLoadingPadrao(true);

      try {
        const config = await obterConfiguracaoListagemPadrao(rotaListagem);
        if (!cancelled && config) {
          handleAplicarConfiguracao(config);
        }
      } catch (error) {
        console.error('Erro ao carregar configuracao padrao de listagem:', error);
      } finally {
        if (!cancelled) {
          setLoadingPadrao(false);
          notificarReadyUmaVez();
        }
        window.clearTimeout(timeoutId);
      }
    };

    carregarPadrao();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [enabled, rotaListagem, handleAplicarConfiguracao]);

  const abrirListagemModal = useCallback(async () => {
    setShowListagemModal(true);
    await listarConfigs();
  }, [listarConfigs]);

  const fecharListagemModal = useCallback(() => {
    setShowListagemModal(false);
  }, []);

  const abrirSalvarModal = useCallback(() => {
    setShowSalvarModal(true);
  }, []);

  const fecharSalvarModal = useCallback(() => {
    setShowSalvarModal(false);
  }, []);

  const selecionarConfig = useCallback((config) => {
    handleAplicarConfiguracao(config);
    setShowListagemModal(false);
    notification.success('Configuracao aplicada com sucesso.', 'Configuracoes');
  }, [handleAplicarConfiguracao]);

  const salvarConfig = useCallback(async ({ nome, geral, padrao }) => {
    if (!enabled || !rotaListagem || !listagemConfig) {
      return null;
    }

    setLoadingSalvar(true);

    // Rascunho atual do painel (valores digitados, mesmo sem "Pesquisar");
    // fallback para os valores aplicados quando o painel nao expoe o rascunho.
    const valoresParaSalvar = obterValoresAtuais?.() ?? valuesRef.current;

    try {
      const criada = await criarConfiguracaoListagem({
        rotaListagem,
        nome,
        geral,
        padrao,
        filtros: buildFiltrosParaPersistencia(valoresParaSalvar, filters, listagemConfig),
        colunas: buildColunasParaPersistencia({
          listagemConfig,
          visibleColumnIds: visibleColumnIdsRef.current,
          columnVisibilityOverride: columnVisibilityOverrideRef.current,
          columnLayoutSnapshot: columnLayoutSnapshotRef.current
        }),
        ordenacao: buildOrdenacaoParaPersistencia(valoresParaSalvar)
      });

      notification.success('Configuracao salva com sucesso.', 'Configuracoes');
      setShowSalvarModal(false);

      if (padrao) {
        handleAplicarConfiguracao(criada);
      }

      return criada;
    } catch (error) {
      console.error('Erro ao salvar configuracao de listagem:', error);
      notification.error(extractConfiguracaoListagemError(error), 'Configuracoes');
      throw error;
    } finally {
      setLoadingSalvar(false);
    }
  }, [enabled, filters, handleAplicarConfiguracao, listagemConfig, obterValoresAtuais, rotaListagem]);

  const excluirConfig = useCallback(async (config) => {
    if (!config?.id) {
      return;
    }

    const confirmar = window.confirm(`Deseja excluir a configuracao "${config.nome}"?`);
    if (!confirmar) {
      return;
    }

    setLoadingExcluirId(config.id);

    try {
      await excluirConfiguracaoListagem(config.id);
      notification.success('Configuracao excluida com sucesso.', 'Configuracoes');
      await listarConfigs();
    } catch (error) {
      console.error('Erro ao excluir configuracao de listagem:', error);
      notification.error(extractConfiguracaoListagemError(error), 'Configuracoes');
    } finally {
      setLoadingExcluirId(null);
    }
  }, [listarConfigs]);

  const handleColumnLayoutChange = useCallback((layout) => {
    columnLayoutSnapshotRef.current = layout;
  }, []);

  const contextValue = useMemo(() => {
    if (!enabled) {
      return null;
    }

    return {
      enabled: true,
      visibleColumnIds,
      columnVisibilityOverride,
      columnConfigKey: listagemConfig?.columnConfigKey,
      onColumnLayoutChange: handleColumnLayoutChange
    };
  }, [
    columnVisibilityOverride,
    enabled,
    handleColumnLayoutChange,
    listagemConfig?.columnConfigKey,
    visibleColumnIds
  ]);

  const modalProps = useMemo(() => ({
    showListagemModal,
    fecharListagemModal,
    configs,
    loadingListagem,
    selecionarConfig,
    excluirConfig,
    loadingExcluirId,
    showSalvarModal,
    fecharSalvarModal,
    salvarConfig,
    loadingSalvar,
    podeSalvarGeral: podeSalvarGeral === true
  }), [
    configs,
    excluirConfig,
    fecharListagemModal,
    fecharSalvarModal,
    loadingExcluirId,
    loadingListagem,
    loadingSalvar,
    podeSalvarGeral,
    salvarConfig,
    selecionarConfig,
    showListagemModal,
    showSalvarModal
  ]);

  return {
    enabled: enabled === true,
    loadingPadrao,
    abrirListagemModal,
    abrirSalvarModal,
    contextValue,
    modalProps
  };
};
